/**
 * Underwriting Feedback Configuration
 * 
 * This is the SINGLE SOURCE OF TRUTH for the underwriting feedback UI.
 * Modify this file to:
 * - Change labels and titles
 * - Add/remove sections
 * - Modify feedback options
 * - Adjust styling
 * 
 * NO component code changes needed for UI modifications!
 */

export const UNDERWRITING_CONFIG = {
  // Section definitions
  sections: [
    {
      id: 'bureau_risk',
      title: 'Bureau (CIBIL)',
      order: 1,
      alwaysShow: true,
      type: 'multi_trigger',
      dataPath: 'bureau_module.raw_output',
      mainGrade: {
        key: 'bureau_risk_grade',
        label: 'Bureau Risk',
        dataPath: 'bureau_risk_grade'
      },
      triggers: [
        {
          key: 'das_grade',
          label: 'DAS Grade',
          dataPath: 'llm_analysis.metrics.das_grade',
          nullable: false
        },
        {
          key: 'tu_leverage_grade',
          label: 'TU Leverage',
          dataPath: 'llm_analysis.metrics.tu_leverage_grade',
          nullable: false
        },
        {
          key: 'credit_risk_grade',
          label: 'Credit Risk',
          dataPath: 'llm_analysis.metrics.credit_risk_grade',
          nullable: false
        },
        {
          key: 'enquiry_trigger',
          label: 'Enquiry Trigger',
          dataPath: 'llm_analysis.metrics.enquiry_history_trigger',
          nullable: true
        },
        {
          key: 'repayment_trigger',
          label: 'Repayment Trigger',
          dataPath: 'llm_analysis.metrics.repayment_capability_trigger',
          nullable: true
        }
      ],
      explanation: {
        label: 'Explanation for Risk Grade',
        source: 'root',
        dataPath: 'summarizer_module.raw_output.simplified_rationales.bureau_rationales'
      },
      feedback: {
        type: 'thumbs',
        label: 'AI Risk Grade',
        question: 'Alignment with Bureau Agent',
        // required: true
      }
    },
    {
      id: 'banking_risk',
      title: 'Banking',
      order: 2,
      alwaysShow: true,
      type: 'multi_trigger',
      dataPath: 'banking_module.raw_output',
      mainGrade: {
        key: 'banking_risk_grade',
        label: 'Banking Risk',
        dataPath: 'banking_risk_grade'
      },
      triggers: [
        {
          key: 'debit_credit_txn_volume',
          label: 'Debit/Credit Volume',
          dataPath: 'llm_analysis.grades.debit_credit_txn_volume',
          nullable: false
        },
        {
          key: 'growth_of_bank_balance',
          label: 'Balance Growth',
          dataPath: 'llm_analysis.grades.growth_of_bank_balance',
          nullable: false
        },
        {
          key: 'seasonality',
          label: 'Seasonality',
          dataPath: 'llm_analysis.grades.seasonality',
          nullable: false
        },
        {
          key: 'irregularity',
          label: 'Irregularity',
          dataPath: 'llm_analysis.grades.irregularity',
          nullable: false
        },
        {
          key: 'financial_stress',
          label: 'Financial Stress',
          dataPath: 'llm_analysis.grades.financial_stress',
          nullable: false
        }
      ],
      explanation: {
        label: 'Explanation for Risk Grade',
        source: 'root',
        dataPath: 'summarizer_module.raw_output.simplified_rationales.banking_rationales'
      },
      feedback: {
        type: 'thumbs',
        label: 'AI Risk Grade',
        question: 'Alignment with Banking Agent',
        // required: true
      }
    },
    {
      id: 'pd_status',
      title: 'Personal Discussion (PD)',
      order: 3,
      alwaysShow: false,
      toolName: 'QnA_Tool',
      requiredPath: 'tool_selector_module.raw_output.required_tools.QnA_Tool.required',
      dataPath: 'toolbox_results.personal_discussion_qna',
      displayGrade: {
        // dataPath: 'toolbox_results.personal_discussion_qna.risk_grade'
      },
      explanation: {
        label: 'Explanation for Risk Grade',
        source: 'root',
        dataPath: 'summarizer_module.raw_output.simplified_rationales.summarised_qna_points',
      formatter: (data) =>
        Array.isArray(data) && data.length ? data : ['Insufficient data available']
      },
      // feedback: {
      //   type: 'thumbs',
      //   // label: 'PD Assessment',
      //   question: 'Alignment with PD Tool',
      //   required: false
      // }
    },
    {
      id: 'fi_status',
      title: 'Field Investigation (FI)',
      order: 4,
      alwaysShow: false,
      toolName: 'Field_Investigation_Tool',
      requiredPath: 'tool_selector_module.raw_output.required_tools.Field_Investigation_Tool.required',
      dataPath: 'toolbox_results.field_investigation',
      displayGrade: {
        // dataPath: 'toolbox_results.field_investigation.risk_grade'
      },
      explanation: {
        label: 'Explanation for Risk Grade',
        source: 'root',
        dataPath: 'toolbox_results.field_investigation_data',
        formatter: (data) => {
          const bullets = [];
          if (data?.office_fi) {
            bullets.push(`Office: ${data.office_fi.final_decision} - ${data.office_fi.reasoning}`);
          }
          if (data?.residential_fi) {
            bullets.push(`Residential: ${data.residential_fi.final_decision} - ${data.residential_fi.reasoning}`);
          }
          return bullets.length > 0 ? bullets : ['Insufficient data available'];
        }
      },
      // feedback: {
      //   type: 'thumbs',
      //   // label: 'FI Assessment',
      //   question: 'Alignment with FI Tool',
      //   required: false
      // }
    },
    {
      id: 'business_credit',
      title: 'Business Credit Summary',
      order: 5,
      alwaysShow: false,
      toolName: 'Business_Credit_Tool',
      requiredPath: 'tool_selector_module.raw_output.required_tools.Business_Credit_Tool.required',
      dataPath: 'toolbox_results.business_credit_summary',
      triggers: [
        {
          key: 'l6m_credit',
          label: 'L6M business credit',
          dataPath: 'total_business_credit_6m',
          displayType: 'currency'
        },
        {
          key: 'credit_quality',
          label: 'Credit Quality',
          dataPath: 'credit_quality',
          displayType: 'badge'
        }
      ],
      displayGrade: {
        // dataPath: 'toolbox_results.field_investigation.risk_grade'
      },
      explanation: {
        label: 'Explanation for Risk Grade',
        source: 'root',
        dataPath: 'toolbox_results.business_credit_summary',
        formatter: (data) => {
          const bullets = [];
          if (data?.credit_quality) {
            bullets.push(`Credit Quality: ${data.credit_quality}`);
          }
          if (data?.total_business_credit_6m !== undefined && data?.total_business_credit_6m !== null) {
            const formatted = formatCurrency(data.total_business_credit_6m);
            bullets.push(`L6M Business Credit: ${formatted}`);
          }
          return bullets.length > 0 ? bullets : ['Insufficient data available'];
        }
      },
      // feedback: {
      //   type: 'thumbs',
      //   // label: 'Business Credits Assessment',
      //   question: 'Alignment with Business Credit Tool',
      //   required: false
      // }
    },
    {
      id: 'overall_risk',
      title: 'Overall Decision',
      order: 6,
      alwaysShow: true,
      type: 'single_value',
      dataPath: 'underwriting_agent_module.raw_output',
      fields: [
        {
          key: 'overall_risk_grade',
          label: 'Risk Grade',
          dataPath: 'overall_risk_grade',
          displayType: 'badge'
        }
      ],
      explanation: {
        label: 'Explanation for Risk Grade',
        source: 'root',
        dataPath: 'summarizer_module.raw_output.simplified_rationales.overall_underwriting_rationales'
      },
      feedback: {
        type: 'thumbs',
        label: 'AI Analysis',
        question: 'Alignment with overall agent response',
        required: true
      }
    }
  ],
  
  // Global styling configuration
  styling: {
    borderRadius: '12px',        // All cards
    cardSpacing: '20px',
    triggerBoxRadius: '8px',     // Trigger boxes
    feedbackOvalRadius: '20px',  // Feedback selector
    cardPadding: '24px',
    triggerBoxPadding: '12px'
  },
  
  // Risk color mappings
  riskColors: {
    'Very High': '#dc3545',
    'High': '#dc3545',
    'Medium': '#ffc107',
    'Low': '#28a745',
    'Very Low': '#28a745',
    'Not Applicable': '#6c757d'
  },
  
  // Submit configuration
  submit: {
    endpoint: '/feedback',
    method: 'POST',
    buttonLabel: 'Submit Feedback',
    commentsRequired: false,
    commentsLabel: 'Text field for UW comments on the overall agent output',
    commentsPlaceholder: 'Enter your comments here...',
    validationMessage: 'Please provide feedback for all sections before submitting'
  },
  
  // View configuration
  defaultView: 'table' // 'card' or 'table'
};

/**
 * Helper function to get nested value from object using dot notation
 * @param {Object} obj - Source object
 * @param {string} path - Dot-notation path (e.g., 'bureau_module.raw_output.bureau_risk_grade')
 * @returns {*} Value at path or null
 */
/**
 * Securely get a property from an object, preventing prototype pollution
 * @param {Object} current - Source object
 * @param {string} key - Property key
 * @returns {*} Value at key or null
 */
const secureGetObjectProperty = (current, key) => {
  // Block prototype pollution: reject dangerous prototype keys
  if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
    return null;
  }
  
  // Ensure `current` is a non-prototypal plain object
  if (current && typeof current === 'object' && current.constructor === Object) {
    // Only access own properties
    if (Object.prototype.hasOwnProperty.call(current, key)) {
      return current[key];
    }
  }
  return null;
};

export const getValueFromPath = (obj, path) => {
  if (!obj || !path) return null;
  
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    const value = secureGetObjectProperty(current, key);
    if (value !== null) {
      current = value;
    } else {
      return null;
    }
  }
  
  return current;
};

/**
 * Format currency for display
 * @param {number} value - Numeric value
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value);
};
